import { BaseEntity, ValidationErrorInfo, ValidationResult } from "@memberjunction/core";
import { RegisterClass } from "@memberjunction/global";
import { SkipUserComponentVersionEntity } from "../generated/entity_subclasses.js";
import { ProjectStoredSpecificationForClient } from "./component-version-specification.js";

/**
 * Fields that identify a saved component version's content. `(ComponentID, Version)` is the key
 * every replica caches a version under, so once a row is saved these must never change: a changed
 * row would keep being served from caches that have no way to notice. A correction is a new version.
 */
export const IMMUTABLE_COMPONENT_VERSION_FIELDS: readonly string[] = [
    'ComponentID',
    'Version',
    'Specification',
    'SpecificationHash',
];

/** The minimal field shape the immutability check reads. */
export interface ImmutabilityCheckField {
    Name: string;
    Dirty: boolean;
}

/**
 * Returns the names of immutable fields that a save would change. New records are never blocked —
 * they are being written for the first time.
 */
export function FindImmutableComponentVersionChanges(
    isSaved: boolean,
    fields: readonly ImmutabilityCheckField[]
): string[] {
    if (!isSaved) {
        return [];
    }
    return fields
        .filter(f => f.Dirty && IMMUTABLE_COMPONENT_VERSION_FIELDS.includes(f.Name))
        .map(f => f.Name);
}

/**
 * User Component Versions are immutable once saved. Skip serves exact-version requests from a
 * per-replica cache keyed by `(ComponentID, Version)` without consulting the database, so an
 * in-place edit (from MJ Explorer, Skip Admin, or a future code path) would never reach replicas
 * that already hold the old content. This guard turns that convention into a rule: Save() fails
 * with a field-level validation error instead.
 *
 * Direct SQL UPDATEs bypass it; nothing in Skip issues them.
 *
 * `Specification` reads return the client projection; `StoredSpecification` returns the stored JSON.
 */
@RegisterClass(BaseEntity, 'User Component Versions')
export class SkipUserComponentVersionEntityExtended extends SkipUserComponentVersionEntity {
    private _projectedFrom?: string;
    private _projected?: string;

    /**
     * The specification as clients see it: the stored JSON without the Data Strategist's declarations (each query's
     * `grain` stays). Every reader of a version goes through this getter, so no reader can leak them. Skip code that
     * needs the declarations reads {@link StoredSpecification}.
     */
    public override get Specification(): string {
        const stored = this.Get('Specification') as string;
        if (stored !== this._projectedFrom) {
            this._projectedFrom = stored;
            this._projected = ProjectStoredSpecificationForClient(stored);
        }
        return this._projected ?? stored;
    }
    public override set Specification(value: string) {
        this.Set('Specification', value);
    }

    /** The specification exactly as stored, declarations included (plan §5 C19). */
    public get StoredSpecification(): string {
        return this.Get('Specification') as string;
    }

    public override Validate(): ValidationResult {
        const result = super.Validate();
        const changed = FindImmutableComponentVersionChanges(this.IsSaved, this.Fields);
        for (const fieldName of changed) {
            result.Errors.push(new ValidationErrorInfo(
                fieldName,
                `${fieldName} cannot be changed on a saved component version. Save a new version instead.`,
                this.Get(fieldName)
            ));
        }
        if (changed.length > 0) {
            result.Success = false;
        }
        return result;
    }
}
